import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2 } from 'lucide-react';
import type { ICreateReelModalProps } from '@/@types/reels';

export function CreateReelModal({ open, onOpenChange }: ICreateReelModalProps) {
  const navigate = useNavigate();
  const createReel = useMutation(api.reels.createReelFromScript);
  const [script, setScript] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!script.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const reelId = await createReel({ script: script.trim() });
      onOpenChange(false);
      setScript('');
      navigate(`/reel/${reelId}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Reel from Text</DialogTitle>
          <DialogDescription>
            Paste your text below. It will be automatically split into scenes.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          placeholder="Paste your text here..."
          value={script}
          onChange={e => setScript(e.target.value)}
          className="flex-1 resize-none min-h-0 focus-visible:ring-0 focus-visible:border-input"
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!script.trim() || isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Reel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
